import React, { useCallback, useMemo, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { NumberInput } from "components/forms/Number-input/NumberInput";
import { currenciesArray } from "constants/index";
import { CharacteristicDef } from "store/types";

import { LocalCharacteristicData } from "../context/DealCreateContext";

interface Props {
  characteristic: CharacteristicDef;
  initialEntries: LocalCharacteristicData[];
  onApply: (entries: LocalCharacteristicData[]) => void;
  onClose: () => void;
}

let nextLocalId = 1;
const generateLocalId = () => `multi_${Date.now()}_${nextLocalId++}`;

export const MultipleCharacteristicPopup: React.FC<Props> = ({ characteristic, initialEntries, onApply, onClose }) => {
  const { id: charId, name, data_type, list_options } = characteristic;
  const [localEntries, setLocalEntries] = useState<LocalCharacteristicData[]>(initialEntries);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const totalPercentage = useMemo(() => localEntries.reduce((acc, item) => acc + item.percentage, 0), [localEntries]);

  const addEntry = () => {
    const remaining = 100 - totalPercentage;
    const newEntry: LocalCharacteristicData = {
      localId: generateLocalId(),
      characteristic_id: charId,
      value: "",
      currency: null,
      percentage: Math.max(0, remaining),
      is_default: localEntries.length === 0
    };
    setLocalEntries((prev) => [...prev, newEntry]);
    setTimeout(() => {
      containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight + 50, behavior: "smooth" });
    }, 100);
  };

  const removeEntry = (localId: string) => {
    setLocalEntries((prev) => prev.filter((e) => e.localId !== localId));
  };

  const updateEntry = (localId: string, updates: Partial<Pick<LocalCharacteristicData, "value" | "percentage">>) => {
    setLocalEntries((prev) => prev.map((e) => (e.localId === localId ? { ...e, ...updates } : e)));
  };

  const spreadEqually = useCallback(() => {
    if (!localEntries.length) return;
    const count = localEntries.length;
    const base = Number((100 / count).toFixed(2));
    const delta = Number((100 - base * count).toFixed(2));
    setLocalEntries((prev) => prev.map((e, i) => ({ ...e, percentage: i === 0 ? base + delta : base })));
  }, [localEntries.length]);

  const isDisabled = useMemo(() => {
    if (!localEntries.length) return false;
    const hasEmpty = localEntries.some((e) => !e.value);
    return hasEmpty || totalPercentage !== 100;
  }, [localEntries, totalPercentage]);

  const isCurrencyChar = data_type === "currency" && name === "Currency";

  const filterCurrencyOptions = (currentValue: string | null): string[] => {
    const used = localEntries.map((e) => e.value).filter((v) => v && v !== currentValue);
    return currenciesArray.filter((c) => !used.includes(c));
  };

  const renderValueInput = (entry: LocalCharacteristicData) => {
    if (isCurrencyChar) {
      return (
        <Autocomplete
          fullWidth
          options={filterCurrencyOptions(entry.value)}
          value={entry.value || ""}
          onChange={(_, option) => updateEntry(entry.localId, { value: option || "" })}
          disableClearable
          renderInput={(params) => <TextField {...params} label="Currency" size="small" />}
        />
      );
    }

    const options = Array.isArray(list_options) ? list_options : [];

    switch (data_type) {
      case "number":
        return (
          <NumberInput
            fullWidth
            size="small"
            value={entry.value}
            onChange={(e) => updateEntry(entry.localId, { value: e.target.value })}
          />
        );
      case "percentage":
        return (
          <NumberInput
            fullWidth
            size="small"
            value={entry.value}
            onChange={(e) => updateEntry(entry.localId, { value: e.target.value })}
            numberInputProps={{ suffix: "%", decimalScale: 2 }}
          />
        );
      case "currency":
        return (
          <NumberInput
            fullWidth
            size="small"
            value={entry.value}
            onChange={(e) => updateEntry(entry.localId, { value: e.target.value })}
            numberInputProps={{ prefix: "$", thousandSeparator: true }}
          />
        );
      case "list":
        return (
          <Autocomplete
            fullWidth
            freeSolo
            options={options}
            value={entry.value}
            onInputChange={(_, newValue) => updateEntry(entry.localId, { value: newValue })}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
        );
      default:
        return (
          <TextField
            fullWidth
            size="small"
            value={entry.value}
            onChange={(e) => updateEntry(entry.localId, { value: e.target.value })}
          />
        );
    }
  };

  return (
    <Stack sx={{ width: 520, maxHeight: 340, overflow: "hidden", borderRadius: "8px", p: "24px", gap: "20px" }}>
      <Typography sx={{ fontSize: "18px", lineHeight: "24px", color: "#071F35", fontWeight: 400 }}>{name}</Typography>
      <Stack sx={{ gap: "16px", overflow: "hidden" }}>
        <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "16px", color: "#496682", lineHeight: "24px", fontWeight: 400 }}>
            Total percentages should add up to 100%
          </Typography>
          <Button size="md" sx={{ bgcolor: "#f5f6f7" }} onClick={spreadEqually}>
            Spread equally
          </Button>
        </Stack>
        <Stack sx={{ gap: "16px", overflow: "auto", py: "5px" }} ref={containerRef}>
          {localEntries.map((entry) => (
            <Stack key={entry.localId} sx={{ flexDirection: "row", alignItems: "center", gap: "16px" }}>
              {renderValueInput(entry)}
              <NumberInput
                size="small"
                label="Share"
                sx={{ width: 120 }}
                value={entry.percentage}
                onChange={(e) => updateEntry(entry.localId, { percentage: Number(e.target.value) })}
                numberInputProps={{ suffix: "%", allowNegative: false, isNumericString: true, decimalScale: 2 }}
              />
              {localEntries.length > 1 ? (
                <IconButton size="sm" sx={{ bgcolor: "#f5f6f7" }} onClick={() => removeEntry(entry.localId)}>
                  <CloseIcon />
                </IconButton>
              ) : (
                <Box sx={{ width: 24, flexShrink: 0 }} />
              )}
            </Stack>
          ))}
        </Stack>
        <IconButton onClick={addEntry} sx={{ bgcolor: "#f5f6f7" }}>
          <CloseIcon sx={{ transform: "rotate(45deg)" }} />
        </IconButton>
      </Stack>
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "15px", lineHeight: "20px", color: "#496682" }}>
          Total: {totalPercentage}%
        </Typography>
        <Stack sx={{ flexDirection: "row", gap: "8px" }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isDisabled} variant="contained" onClick={() => onApply(localEntries)}>
            Apply
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
